import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import apiClient from '../../api/apiClient';
import { OPENAI_CHAT_MODELS } from '../../constants/openaiModels';
import { CLAUDE_MODEL_OPTIONS, GEMINI_MODEL_OPTIONS } from '../../constants/providerModels';

function trimSnapshot(flowData) {
  const limit = 1200;
  const trim = (value) => {
    if (typeof value === 'string') return value.length > limit ? `${value.slice(0, limit)}…` : value;
    if (Array.isArray(value)) return value.map(trim);
    if (!value || typeof value !== 'object') return value;
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      output[key] = trim(item);
    });
    return output;
  };

  return {
    name: flowData.name,
    steps: (flowData.steps || []).map((step) => trim({
      id: step.id,
      name: step.name,
      type: step.type,
      order: step.order,
      nextStepId: step.nextStepId || null,
      position: step.position,
      config: step.config,
      variableMapping: step.variableMapping,
      variableOperations: step.variableOperations
    }))
  };
}

const FlowAgentPanel = ({
  projectId,
  flowId,
  flowData,
  apiEndpoints,
  onApplyPatch,
  onClose
}) => {
  const [draft, setDraft] = useState('');
  const [logExcerpt, setLogExcerpt] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [model, setModel] = useState('gpt-4o');

  const send = async () => {
    const message = draft.trim();
    if (!message || busy) return;
    if (!flowId) {
      setError('Save the flow once before using the agent.');
      return;
    }

    const history = messages.map((item) => ({ role: item.role, content: item.content }));
    setMessages((current) => [...current, { role: 'user', content: message }]);
    setDraft('');
    setBusy(true);
    setError('');

    try {
      const body = await apiClient.post(
        `/flow-builder/projects/${projectId}/flows/${flowId}/agent`,
        {
          message,
          history,
          flowSnapshot: trimSnapshot(flowData),
          apiEndpoints: (apiEndpoints || []).map((endpoint) => ({
            id: endpoint._id || endpoint.id,
            name: endpoint.name,
            method: endpoint.method,
            url: endpoint.url
          })),
          logExcerpt,
          model
        }
      );
      const result = body?.data || body;
      if (result?.patch) onApplyPatch(result.patch, result.highlightStepIds || []);
      setMessages((current) => [...current, {
        role: 'assistant',
        content: result?.reply || 'The agent finished without a reply.',
        trace: result?.trace || []
      }]);
    } catch (requestError) {
      const serverMessage = requestError.response?.data?.message || requestError.message || 'The agent request failed.';
      setError(serverMessage);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{
      width: 400,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderLeft: '1px solid #e5e7eb',
      backgroundColor: '#fff',
      flexShrink: 0
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Flow agent</Typography>
          <Typography variant="caption" color="text.secondary">
            Edits stay on the canvas until you click Save Flow.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close agent">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
        <FormControl fullWidth size="small">
          <InputLabel id="flow-agent-model-label">Model</InputLabel>
          <Select
            labelId="flow-agent-model-label"
            label="Model"
            value={model}
            onChange={(event) => setModel(event.target.value)}
            disabled={busy}
          >
            <ListSubheader>OpenAI</ListSubheader>
            {OPENAI_CHAT_MODELS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
            <ListSubheader>Claude</ListSubheader>
            {CLAUDE_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
            <ListSubheader>Gemini</ListSubheader>
            {GEMINI_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Ask the agent to explain this flow, fix a node, or add a path. It reviews the steps, plans the change, and checks the result before anything appears on the canvas.
          </Typography>
        )}
        {messages.map((item, index) => (
          <Box key={`${item.role}-${index}`} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: item.role === 'user' ? '#1d4ed8' : '#047857' }}>
              {item.role === 'user' ? 'You' : 'Agent'}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.content}</Typography>
            {item.trace?.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {item.trace.map((entry, traceIndex) => (
                  <Typography key={`${entry.role}-${traceIndex}`} variant="caption" display="block" color="text.secondary">
                    {entry.role}: {entry.summary || entry.status}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}
        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Running the concierge, managers, workers, and QC.</Typography>
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <TextField
          value={logExcerpt}
          onChange={(event) => setLogExcerpt(event.target.value)}
          placeholder="Optional server log excerpt"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          sx={{ mb: 1 }}
        />
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Describe the flow change"
          multiline
          minRows={2}
          maxRows={5}
          fullWidth
          size="small"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={send}
          disabled={busy || !draft.trim()}
          sx={{ mt: 1 }}
          fullWidth
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default FlowAgentPanel;
